import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import "./AggregatedUserCharts.css";
import "chartjs-adapter-date-fns";

function AggregatedUserCharts({
  allScores,
  startDate,
  endDate,
  selectedOption,
}) {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const scatterCanvasRef = useRef(null);
  const scatterChartRef = useRef(null);

  function getAveragedScores(scores, quizType) {
    let sum = { Cognitive: 0, Affective: 0, "Empathic Response": 0 };
    let max = { Cognitive: 0, Affective: 0, "Empathic Response": 0 };
    let count = 0;
    scores.forEach((entry) => {
      if (entry.quiz === quizType && entry.score && entry.maxScore) {
        sum.Cognitive += entry.score.Cognitive;
        sum.Affective += entry.score.Affective;
        sum["Empathic Response"] += entry.score["Empathic Response"];
        max.Cognitive += entry.maxScore.Cognitive;
        max.Affective += entry.maxScore.Affective;
        max["Empathic Response"] += entry.maxScore["Empathic Response"];
        count++;
      }
    });
    // Average
    Object.keys(sum).forEach((key) => {
      sum[key] = count ? sum[key] / count : 0;
      max[key] = count ? max[key] / count : 0;
    });
    return { sum, max };
  }

  // most recent scores graphed as radar
  useEffect(() => {
    const prac = getAveragedScores(allScores, "practitioner");
    const user = getAveragedScores(allScores, "user");

    const data = {
      labels: ["Cognitive", "Affective", "Empathic Response"],
      datasets: [
        {
          label: "Practitioner-to-Practitioner",
          data: [
            prac.max.Cognitive
              ? Math.round((prac.sum.Cognitive / prac.max.Cognitive) * 100)
              : 0,
            prac.max.Affective
              ? Math.round((prac.sum.Affective / prac.max.Affective) * 100)
              : 0,
            prac.max["Empathic Response"]
              ? Math.round(
                  (prac.sum["Empathic Response"] /
                    prac.max["Empathic Response"]) *
                    100
                )
              : 0,
          ],
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgb(75, 192, 192)",
          pointBackgroundColor: "rgb(75, 192, 192)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Practitioner-to-User",
          data: [
            user.max.Cognitive
              ? Math.round((user.sum.Cognitive / user.max.Cognitive) * 100)
              : 0,
            user.max.Affective
              ? Math.round((user.sum.Affective / user.max.Affective) * 100)
              : 0,
            user.max["Empathic Response"]
              ? Math.round(
                  (user.sum["Empathic Response"] /
                    user.max["Empathic Response"]) *
                    100
                )
              : 0,
          ],
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          borderColor: "rgb(255, 99, 132)",
          pointBackgroundColor: "rgb(255, 99, 132)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const config = {
      type: "radar",
      data: data,
      options: {
        responsive: true,
        layout: { padding: { bottom: 0 } },
        plugins: { legend: { position: "top" } },
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: { stepSize: 20 },
          },
        },
      },
    };

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current, config);
  }, [allScores]);

  useEffect(() => {
    const pracScores = [];
    const userScores = [];

    allScores.forEach((entry) => {
      const date = new Date(entry.date?.$date || entry.date);
      const s = entry.score;
      const m = entry.maxScore;
      if (
        s &&
        m &&
        ["Cognitive", "Affective", "Empathic Response"].every(
          (key) => s[key] !== undefined && m[key] !== undefined && m[key] !== 0
        )
      ) {
        const totalScore =
          ((s["Cognitive"] / m["Cognitive"] +
            s["Affective"] / m["Affective"] +
            s["Empathic Response"] / m["Empathic Response"]) /
            3) *
          100;
        const point = {
          x: date,
          y: Math.round(totalScore),
        };
        if (entry.quiz === "practitioner") {
          pracScores.push(point);
        } else if (entry.quiz === "user") {
          userScores.push(point);
        }
      }
    });

    const data = {
      datasets: [
        {
          label: "Practitioner-to-Practitioner",
          data: pracScores,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgb(75, 192, 192)",
          showLine: false,
          tension: 0.3,
        },
        {
          label: "Practitioner-to-User",
          data: userScores,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgb(255, 99, 132)",
          showLine: false,
          tension: 0.3,
        },
      ],
    };

    var startDateObj = null;
    var endDateObj = null;

    if (startDate && startDate !== "") {
      const [year, month, day] = startDate.split("-");
      // Set to 14:00 UTC on the previous day to get 00:00 AEST on the selected date
      const date = new Date(Date.UTC(year, month - 1, day, 14, 0, 0));
      date.setUTCDate(date.getUTCDate() - 1);
      startDateObj = date;
    }

    if (endDate && endDate !== "") {
      const [year, month, day] = endDate.split("-");
      // 11:59:59.999pm AEST is 13:59:59.999 UTC on the selected day
      const date = new Date(Date.UTC(year, month - 1, day, 13, 59, 59, 999));
      endDateObj = date;
    }

    const scatterConfig = {
      type: "scatter",
      data,
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: "Total Normalised Scores Over Time",
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: {
                day: "dd/MM",
              },
            },
            title: {
              display: true,
              text: "Date",
            },
            min: startDateObj,
            max: endDateObj,
          },
          y: {
            title: {
              display: true,
              text: "Total Normalised Score (%)",
            },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      },
    };

    if (scatterChartRef.current) {
      scatterChartRef.current.destroy();
    }

    scatterChartRef.current = new Chart(
      scatterCanvasRef.current,
      scatterConfig
    );
  }, [allScores, startDate, endDate]);

  const exportScatterToCSV = (data, filename) => {
    const csvRows = [];
    const headers = [
      "Quiz",
      "Date",
      "Total Normalised Score",
      "Cognitive Score",
      "Affective Score",
      "Empathic Response Score",
    ];
    csvRows.push(headers.join(","));

    data.forEach((point) => {
      const row = [
        point.quiz,
        point.x instanceof Date ? point.x.toISOString() : point.x,
        point.y,
        ((point.cognitiveRaw / point.cognitiveMax) * 100).toFixed(1) ?? "",
        ((point.affectiveRaw / point.affectiveMax) * 100).toFixed(1) ?? "",
        ((point.empathicRaw / point.empathicMax) * 100).toFixed(1) ?? "",
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportRadarToCSV = (chart, filename) => {
    if (!chart || !chart.data) return;

    const csvRows = [];
    const headers = [
      "Label",
      ...chart.data.labels.map((label) => `${label} Score`),
    ];
    csvRows.push(headers.join(","));

    chart.data.datasets.forEach((dataset) => {
      const row = [dataset.label, ...dataset.data];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // check if user has any existing quiz results
  let noScores = false;
  if (!allScores || allScores.length === 0) {
    noScores = true;
  }

  return (
    <div className="user-charts">
      {!noScores && (
        <div className="charts">
          <h3>Averaged Recent Empathy Scores</h3>
          <canvas ref={canvasRef}></canvas>
          <button
            onClick={() => {
              if (chartRef.current) {
                const link = document.createElement("a");
                link.href = chartRef.current.toBase64Image();
                link.download = "averaged_scores_chart.png";
                link.click();
              }
            }}
          >
            {" "}
            Download PNG{" "}
          </button>
          <button
            onClick={() => {
              if (chartRef.current) {
                exportRadarToCSV(
                  chartRef.current,
                  "radar_chart_data.csv",
                  allScores
                );
              }
            }}
          >
            Export Radar Data to CSV
          </button>
          <h3>Score History</h3>
          <canvas ref={scatterCanvasRef} width={600} height={400}></canvas>
          <button
            onClick={() => {
              if (scatterChartRef.current) {
                const link = document.createElement("a");
                link.href = scatterChartRef.current.toBase64Image();
                link.download = "all_users_score_history_chart.png";
                link.click();
              }
            }}
          >
            {" "}
            Download PNG{" "}
          </button>
          <button
            onClick={() => {
              if (scatterChartRef.current) {
                const allData = [];
                scatterChartRef.current.data.datasets.forEach((dataset) => {
                  dataset.data.forEach((point) => {
                    // Find the matching score entry by date and quiz type
                    let scoreEntry = allScores.find((entry) => {
                      const entryDate = new Date(
                        entry.date?.$date || entry.date
                      ).toISOString();
                      const pointDate =
                        point.x instanceof Date
                          ? point.x.toISOString()
                          : point.x;
                      return (
                        ((dataset.label === "Practitioner-to-Practitioner" &&
                          entry.quiz === "practitioner") ||
                          (dataset.label === "Practitioner-to-User" &&
                            entry.quiz === "user")) &&
                        entryDate === pointDate
                      );
                    });
                    allData.push({
                      quiz: dataset.label,
                      x: point.x,
                      y: point.y,
                      cognitiveRaw: scoreEntry?.score?.["Cognitive"] ?? "",
                      cognitiveMax: scoreEntry?.maxScore?.["Cognitive"] ?? "",
                      affectiveRaw: scoreEntry?.score?.["Affective"] ?? "",
                      affectiveMax: scoreEntry?.maxScore?.["Affective"] ?? "",
                      empathicRaw:
                        scoreEntry?.score?.["Empathic Response"] ?? "",
                      empathicMax:
                        scoreEntry?.maxScore?.["Empathic Response"] ?? "",
                    });
                  });
                });
                exportScatterToCSV(allData, "scatter_chart_data.csv");
              }
            }}
          >
            Export Scatter Data to CSV
          </button>
        </div>
      )}
    </div>
  );
}

export default AggregatedUserCharts;
